import { useState } from 'react'
import { PoolWithMetrics, poolsApi } from '../lib/api'

interface PoolCardProps {
  pool: PoolWithMetrics
  onUpdate: () => void
}

export default function PoolCard({ pool, onUpdate }: PoolCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [concurrency, setConcurrency] = useState(pool.targetConcurrency)
  const [saving, setSaving] = useState(false)
  const [togglingAutoPilot, setTogglingAutoPilot] = useState(false)

  const autoPilotEnabled = pool.autoPilotConfig?.enabled ?? false

  const handleToggleAutoPilot = async () => {
    setTogglingAutoPilot(true)
    try {
      await poolsApi.updateAutoPilot(pool.id, { enabled: !autoPilotEnabled })
      onUpdate()
    } catch (error) {
      console.error('Failed to toggle auto-pilot:', error)
    } finally {
      setTogglingAutoPilot(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await poolsApi.update(pool.id, { targetConcurrency: concurrency })
      setIsEditing(false)
      onUpdate()
    } catch (error) {
      console.error('Failed to update pool:', error)
    } finally {
      setSaving(false)
    }
  }

  const providerBadge = {
    cloudrun: 'bg-blue-100 text-blue-800',
    flyio: 'bg-purple-100 text-purple-800',
    onprem: 'bg-orange-100 text-orange-800',
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{pool.name}</h3>
          <p className="text-sm text-gray-500">{pool.region}</p>
        </div>
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            providerBadge[pool.provider]
          }`}
        >
          {pool.provider}
        </span>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center p-3 bg-gray-50 rounded">
          <p className="text-2xl font-bold text-gray-900">{pool.activeWorkers}</p>
          <p className="text-xs text-gray-500">Active</p>
        </div>
        <div className="text-center p-3 bg-gray-50 rounded">
          <p className="text-2xl font-bold text-gray-900">{pool.idleWorkers}</p>
          <p className="text-xs text-gray-500">Idle</p>
        </div>
        <div className="text-center p-3 bg-gray-50 rounded">
          <p className="text-2xl font-bold text-gray-900">{pool.jobsProcessed24h}</p>
          <p className="text-xs text-gray-500">Jobs (24h)</p>
        </div>
        <div className="text-center p-3 bg-gray-50 rounded">
          <p className="text-2xl font-bold text-gray-900">
            {Math.round(pool.avgProcessingTimeMs / 1000)}s
          </p>
          <p className="text-xs text-gray-500">Avg Time</p>
        </div>
      </div>

      {/* Configuration */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Target Concurrency</span>
          {isEditing ? (
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="1"
                max="10"
                value={concurrency}
                onChange={(e) => setConcurrency(parseInt(e.target.value) || 1)}
                className="w-16 px-2 py-1 border rounded text-sm"
              />
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-2 py-1 bg-brand-600 text-white text-sm rounded hover:bg-brand-700 disabled:opacity-50"
              >
                {saving ? '...' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setConcurrency(pool.targetConcurrency)
                  setIsEditing(false)
                }}
                className="px-2 py-1 text-gray-600 text-sm hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-900">{pool.targetConcurrency}</span>
              <button
                onClick={() => setIsEditing(true)}
                className="text-brand-600 text-sm hover:text-brand-700"
              >
                Edit
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Instances</span>
          <span className="text-gray-900">
            {pool.minInstances} - {pool.maxInstances}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm mt-1">
          <span className="text-gray-500">Status</span>
          <span className={pool.enabled ? 'text-green-600' : 'text-red-600'}>
            {pool.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        {/* Auto-Pilot Toggle */}
        <div className="flex items-center justify-between text-sm mt-3 pt-3 border-t">
          <div>
            <span className="text-gray-700 font-medium">Auto-Pilot</span>
            <p className="text-xs text-gray-500">Automatic scaling</p>
          </div>
          <button
            type="button"
            onClick={handleToggleAutoPilot}
            disabled={togglingAutoPilot}
            title={autoPilotEnabled ? 'Disable auto-pilot' : 'Enable auto-pilot'}
            aria-label={autoPilotEnabled ? 'Disable auto-pilot' : 'Enable auto-pilot'}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 ${
              autoPilotEnabled ? 'bg-brand-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                autoPilotEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  )
}
